import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link, useSearchParams, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import VoiceDictationButton from "../components/VoiceDictationButton";
import DicomViewer from "../components/DicomViewer";
import DicomUploadManager from "../components/DicomUploadManager";
import DicomSliceBrowser from "../components/DicomSliceBrowser";
import CaseAiSummary from "../components/CaseAiSummary";
import ReviewCard from "../components/ReviewCard";
import BookVideoFollowup from "../components/BookVideoFollowup";
import StructuredOpinionEditor from "../components/StructuredOpinionEditor";
import TATBadge from "../components/TATBadge";
import { TEMPLATE_BY_ID } from "../lib/reportTemplates";
import { useI18n } from "../lib/i18n";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { api, API, getToken } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { FileText, Send, ArrowLeft, Stethoscope, Video, X, Sparkles, PhoneCall, MessageCircle, Phone, ClipboardPlus, Mic, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { PrescriptionDialog, PrescriptionsList } from "../components/Prescription";

export default function CaseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { lang } = useI18n();
  const [data, setData] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [opinion, setOpinion] = useState({ diagnosis_summary: "", recommendations: "", prescription: "", follow_up_required: false });
  // Phase 2 — structured opinion state. `mode: "structured"` (default) or "freeform".
  const [opinionMode, setOpinionMode] = useState("structured");
  const [structured, setStructured] = useState(null);
  const bottomRef = useRef(null);
  const [videoOpen, setVideoOpen] = useState(false);
  const [rxOpen, setRxOpen] = useState(false);
  const [rxRefresh, setRxRefresh] = useState(0);
  const roomName = `noi-${id}`;
  const ringingRef = useRef({ ctx: null, interval: null, started: false });

  const load = async () => {
    const [c, m] = await Promise.all([api.get(`/consultations/${id}`), api.get(`/messages/${id}`)]);
    setData(c.data); setMsgs(m.data);
    if (c.data.opinion) setOpinion({
      diagnosis_summary: c.data.opinion.diagnosis_summary || "",
      recommendations: c.data.opinion.recommendations || "",
      prescription: c.data.opinion.prescription || "",
      follow_up_required: !!c.data.opinion.follow_up_required,
    });
    if (c.data.opinion?.structured) {
      setStructured(c.data.opinion.structured);
      setOpinionMode("structured");
    }
  };
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); /* eslint-disable-next-line */ }, [id]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs.length]);

  const send = async () => {
    if (!text.trim()) return;
    try { await api.post("/messages", { consultation_id: id, text }); setText(""); load(); }
    catch { toast.error("Send failed"); }
  };

  // ---- AI polish: structure the doctor's raw draft into a clean report ----
  const [polishBusy, setPolishBusy] = useState(false);
  const polishOpinion = async () => {
    if (!opinion.diagnosis_summary.trim() && !opinion.recommendations.trim() && !opinion.prescription.trim()) {
      toast.error("Write at least a rough note first — then I'll polish it.");
      return;
    }
    setPolishBusy(true);
    try {
      const r = await api.post("/ai/polish-opinion", {
        diagnosis_summary: opinion.diagnosis_summary,
        recommendations: opinion.recommendations,
        prescription: opinion.prescription,
        language: data?.language || lang || "en",
      });
      setOpinion({ ...opinion, ...r.data });
      toast.success("Polished — review and submit when you're happy.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI polish unavailable. Save manually.");
    } finally {
      setPolishBusy(false);
    }
  };

  const submitOpinion = async () => {
    // Build payload — if structured mode + non-empty fields, also derive
    // legacy free-text so the existing patient report viewer still works.
    let payload = { ...opinion };
    if (opinionMode === "structured" && structured?.fields) {
      const f = structured.fields;
      const flat = (txt) => (txt || "").trim();
      const dx = flat(f.diagnosis) || flat(f.stroke_type) || flat(f.seizure_type) || flat(f.findings_summary);
      const recParts = [
        f.surgical_recommendation, f.acute_management, f.conservative_trial,
        f.dmt_recommendation, f.medications, f.alternatives, f.plan,
      ].map(flat).filter(Boolean);
      payload = {
        diagnosis_summary: opinion.diagnosis_summary?.trim() || dx || "(See structured report)",
        recommendations: opinion.recommendations?.trim() || recParts.join("\n\n") || "(See structured report)",
        prescription: opinion.prescription || flat(f.medications) || "",
        follow_up_required: opinion.follow_up_required,
        structured: structured,
      };
    }
    if (!payload.diagnosis_summary?.trim() || !payload.recommendations?.trim()) {
      toast.error("Please fill the diagnosis and recommendation fields");
      return;
    }
    try { await api.post(`/consultations/${id}/opinion`, payload); toast.success("Report sent to patient"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Submit failed"); }
  };

  // ---- Video call: signal the other party, then open Jitsi iframe ----
  const joinCall = async () => {
    stopRinging();
    try { await api.post(`/consultations/${id}/call/start`); }
    catch (err) { console.warn("[call/start]", err?.message || err); /* non-fatal: still open the room */ }
    setVideoOpen(true);
    // strip ?call=1 so reloads don't re-trigger auto-join
    if (searchParams.get("call")) {
      searchParams.delete("call");
      setSearchParams(searchParams, { replace: true });
    }
    load();
  };

  // Is a call active right now? (started by anyone within the last 15 min)
  const callActive = useMemo(() => {
    if (!data?.call_started_at) return false;
    const started = new Date(data.call_started_at).getTime();
    return Date.now() - started < 15 * 60 * 1000;
  }, [data?.call_started_at]);
  const callStartedByMe = data?.call_started_by === user?.id;
  const incomingCall = callActive && !callStartedByMe && !videoOpen;

  // ---- Ringing sound (Web Audio API, no asset needed) ----
  const playRingingTone = () => {
    const state = ringingRef.current;
    if (state.started) return;
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      const ctx = new AC();
      state.ctx = ctx;
      const tone = () => {
        const now = ctx.currentTime;
        // Two-tone ring: 480Hz + 620Hz (classic phone ring)
        [480, 620].forEach((freq) => {
          const o = ctx.createOscillator();
          const g = ctx.createGain();
          o.type = "sine"; o.frequency.value = freq;
          g.gain.setValueAtTime(0, now);
          g.gain.linearRampToValueAtTime(0.18, now + 0.02);
          g.gain.linearRampToValueAtTime(0, now + 0.8);
          o.connect(g).connect(ctx.destination);
          o.start(now); o.stop(now + 0.85);
        });
      };
      tone();
      state.interval = setInterval(tone, 2500);
      state.started = true;
    } catch (err) {
      // autoplay is blocked until the user interacts with the page — that's fine
      console.debug("[ringing] autoplay deferred:", err?.message || err);
    }
  };
  const stopRinging = () => {
    const state = ringingRef.current;
    if (state.interval) { clearInterval(state.interval); state.interval = null; }
    if (state.ctx) {
      try { state.ctx.close(); }
      catch (err) { console.debug("[ringing] close ignored:", err?.message || err); }
      state.ctx = null;
    }
    state.started = false;
  };
  useEffect(() => {
    if (incomingCall) {
      // Vibrate (mobile) and play ring
      if (navigator.vibrate) navigator.vibrate([400, 200, 400, 200, 400]);
      playRingingTone();
      // Show browser notification if granted (works when tab is in background)
      if (typeof Notification !== "undefined" && Notification.permission === "granted") {
        try { new Notification("📹 Incoming video call", { body: "Tap to join your consultation", tag: `call-${id}` }); }
        catch (err) { console.debug("[notify]", err?.message || err); }
      } else if (typeof Notification !== "undefined" && Notification.permission === "default") {
        Notification.requestPermission().catch(() => {});
      }
    } else {
      stopRinging();
    }
    return stopRinging;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomingCall]);

  // Auto-open call when deep-linked via ?call=1 (SMS / WhatsApp tap)
  useEffect(() => {
    if (data && searchParams.get("call") === "1" && !videoOpen) {
      setVideoOpen(true);
      // If no active call yet, also signal start
      if (!callActive) { joinCall(); }
      else {
        searchParams.delete("call");
        setSearchParams(searchParams, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.id]);

  const startReview = async () => {
    try { await api.post(`/consultations/${id}/start-review`); toast.success("Patient notified"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!data) return (<div className="min-h-screen bg-slate-50"><Navbar/><div className="p-8 text-slate-500">Loading…</div></div>);

  const isDoctor = user?.role === "doctor";
  const t = getToken();
  const backPath = user?.role === "admin" ? "/admin" : isDoctor ? "/doctor" : "/patient";

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Link to={backPath} className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4 mr-1"/> Back
        </Link>
        <div className="mt-2 flex items-center justify-between flex-wrap gap-2">
          <div>
            <h1 className="font-heading text-2xl font-semibold text-slate-900">Case #{data.id.slice(0,8)}</h1>
            <p className="text-sm text-slate-500">
              {data.patient_name} → Dr. {data.doctor_name} · {(data.currency || "INR").toUpperCase() === "USD" ? `$${data.fee} USD` : `₹${data.fee}`} · {data.status}
            </p>
          </div>
          {data.payment_status === "paid" && (
            <TATBadge
              createdAt={data.paid_at || data.created_at}
              slaHours={data.sla_hours || (data.priority === "urgent" || data.service_type === "EMERGENCY" ? 24 : 48)}
              status={data.status}
              completedAt={data.completed_at}
            />
          )}
        </div>

        {/* Emergency or Video Consultation — patient-visible SLA hero so
            they see the promise is being honoured in real time. */}
        {data.payment_status === "paid" && data.status !== "completed" &&
          (data.service_type === "EMERGENCY" || data.priority === "urgent" || data.priority === "video") && (
          <div className="mt-4 rounded-xl border-2 border-red-200 bg-gradient-to-r from-red-50 to-amber-50 p-4 flex items-start gap-3" data-testid="patient-sla-hero">
            <span className="text-3xl">🚨</span>
            <div className="flex-1">
              <div className="font-heading font-bold text-red-800">
                {data.consultation_format === "video"
                  ? "🎥 Video Consultation case"
                  : data.priority === "video"
                    ? "Video Consultation case"
                    : "Priority or Video Consultation case"}
              </div>
              <div className="text-sm text-slate-700 mt-0.5">
                Your specialist is prioritising your case — you'll receive the written report on WhatsApp within&nbsp;
                <b>
                  <TATBadge
                    createdAt={data.paid_at || data.created_at}
                    slaHours={24}
                    status={data.status}
                    completedAt={data.completed_at}
                    compact
                  />
                </b>.
              </div>
              <div className="text-[11px] text-slate-500 mt-1">
                Note: NeuroOpinion offers priority second opinions and does NOT replace emergency medical care.
                For a medical emergency, please visit your nearest hospital immediately.
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
          {/* Left: Case info + files + opinion */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="p-6 border-slate-200">
              <div className="font-heading text-lg font-semibold text-slate-900">Symptoms</div>
              <p className="text-sm text-slate-700 mt-2 whitespace-pre-wrap">{data.symptoms}</p>
              {data.symptom_tags?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {data.symptom_tags.map(tag => (
                    <span key={tag} className="text-xs px-2 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-100">{tag}</span>
                  ))}
                </div>
              )}
              <div className="mt-4 text-xs text-slate-500">
                {data.patient_age && <span>Patient: {data.patient_age}y {data.patient_gender || ""} · </span>}
                {data.state && <span>State: {data.state} · </span>}
                Duration: {data.duration_days} days · Report language: {data.language?.toUpperCase()}
              </div>
            </Card>

            {isDoctor && data.ai_triage && (
              <Card className="p-6 border-blue-200 bg-gradient-to-br from-blue-50/60 to-indigo-50/40" data-testid="ai-triage-card">
                <div className="flex items-center justify-between">
                  <div className="font-heading text-lg font-semibold text-slate-900 flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-blue-700"/> AI triage pre-summary
                  </div>
                  <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-white text-blue-700 border border-blue-200">Haiku 4.5 · preview</span>
                </div>
                <p className="text-xs text-slate-500 mt-1">Assistive only — does not replace your clinical judgement.</p>
                {data.ai_triage.summary && (
                  <div className="mt-3 text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">{data.ai_triage.summary}</div>
                )}
                {data.ai_triage.red_flags?.length > 0 && (
                  <div className="mt-3">
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-red-700">Red flags</div>
                    <ul className="mt-1 list-disc pl-5 text-sm text-slate-800 space-y-0.5">
                      {data.ai_triage.red_flags.map((f, i) => <li key={i}>{f}</li>)}
                    </ul>
                  </div>
                )}
                {data.ai_triage.recommended_focus && (
                  <div className="mt-3 text-sm">
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Recommended focus</span>
                    <div className="text-slate-800">{data.ai_triage.recommended_focus}</div>
                  </div>
                )}
                {data.ai_triage.suggested_scans?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {data.ai_triage.suggested_scans.map((s, i) => (
                      <span key={i} className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-white text-blue-700 border border-blue-200">{s}</span>
                    ))}
                  </div>
                )}
              </Card>
            )}

            {data.consultation_format === "video" && data.video_meet_url && (
              <Card className="p-6 border-violet-200 bg-gradient-to-br from-violet-50 to-fuchsia-50" data-testid="video-meet-card">
                <div className="font-heading text-lg font-semibold text-slate-900 flex items-center gap-2">
                  🎥 Video Consultation room
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Jitsi Meet · same link for doctor & patient · encrypted · no app install
                </p>
                <a
                  href={data.video_meet_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="video-meet-link"
                  className="mt-3 block text-sm font-mono text-violet-800 break-all underline"
                >{data.video_meet_url}</a>
                {data.video_slot_at && (
                  <div className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-violet-800 bg-white border border-violet-200 rounded-full px-3 py-1.5">
                    📅 Scheduled: {new Date(data.video_slot_at).toLocaleString()}
                  </div>
                )}
                <a href={data.video_meet_url} target="_blank" rel="noopener noreferrer" className="block mt-4">
                  <Button className="w-full h-11 bg-violet-700 hover:bg-violet-800 text-white" data-testid="video-join-btn">
                    Join the video room
                  </Button>
                </a>
              </Card>
            )}

            {isDoctor && data.ai_triage_preview && (
              <Card className="p-6 border-amber-200 bg-gradient-to-br from-amber-50/60 to-orange-50/40" data-testid="ai-triage-preview-card">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="font-heading text-lg font-semibold text-slate-900 flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-amber-700"/> AI symptom preview
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {data.ai_triage_preview.urgency && (
                      <span
                        data-testid="ai-preview-urgency"
                        className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                          data.ai_triage_preview.urgency === "urgent"
                            ? "bg-red-600 text-white animate-pulse"
                            : data.ai_triage_preview.urgency === "soon"
                              ? "bg-amber-500 text-white"
                              : "bg-emerald-600 text-white"
                        }`}
                      >
                        {data.ai_triage_preview.urgency}
                      </span>
                    )}
                    <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-white text-amber-700 border border-amber-200">from voice / bot</span>
                  </div>
                </div>
                <p className="text-xs text-slate-500 mt-1">Generated from the patient&apos;s symptom description — assistive only.</p>
                {data.ai_triage_preview.one_liner && (
                  <p className="mt-3 text-sm text-slate-800 leading-relaxed" data-testid="ai-preview-oneliner">{data.ai_triage_preview.one_liner}</p>
                )}
                {data.ai_triage_preview.differential?.length > 0 && (
                  <div className="mt-3">
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Top differentials</div>
                    <ol className="mt-1 list-decimal pl-5 text-sm text-slate-800 space-y-0.5">
                      {data.ai_triage_preview.differential.map((d, i) => (
                        <li key={i} data-testid={`ai-preview-diff-${i}`}>{d}</li>
                      ))}
                    </ol>
                  </div>
                )}
                {data.ai_triage_preview.red_flags?.length > 0 && (
                  <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3">
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-red-700 flex items-center gap-1">
                      <AlertTriangle className="w-3.5 h-3.5"/> Red flags detected
                    </div>
                    <ul className="mt-1 list-disc pl-5 text-sm text-red-800 space-y-0.5">
                      {data.ai_triage_preview.red_flags.map((f, i) => <li key={i}>{f}</li>)}
                    </ul>
                  </div>
                )}
                {data.ai_triage_preview.suggested_specialization && (
                  <div className="mt-3 text-xs text-slate-600">
                    Suggested specialty: <span className="font-semibold text-slate-800">{data.ai_triage_preview.suggested_specialization}</span>
                  </div>
                )}
              </Card>
            )}

            {data.wa_voice_notes?.length > 0 && (
              <Card className="p-6 border-teal-200 bg-gradient-to-br from-teal-50/60 to-cyan-50/40" data-testid="patient-voice-notes-card">
                <div className="font-heading text-lg font-semibold text-slate-900 flex items-center gap-2">
                  <Mic className="w-5 h-5 text-teal-700"/> Patient voice notes
                  <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-white text-teal-700 border border-teal-200">
                    {data.wa_voice_notes.length}
                  </span>
                </div>
                <p className="text-xs text-slate-500 mt-1">Play the original recording to catch tone, hesitation, and details the transcript may have missed.</p>
                <div className="mt-4 space-y-4">
                  {data.wa_voice_notes.map((v, i) => {
                    // Language badge — human-readable label + colour tone
                    // for the 3 languages we actively target (EN/HI/TE).
                    const LANG_MAP = {
                      en: { label: "English", tone: "bg-blue-100 text-blue-800 border-blue-200" },
                      hi: { label: "हिन्दी · Hindi", tone: "bg-orange-100 text-orange-800 border-orange-200" },
                      te: { label: "తెలుగు · Telugu", tone: "bg-emerald-100 text-emerald-800 border-emerald-200" },
                    };
                    const lang = (v.language || "").toLowerCase();
                    const badge = LANG_MAP[lang] || (lang ? { label: lang.toUpperCase(), tone: "bg-slate-100 text-slate-700 border-slate-200" } : null);
                    return (
                      <div key={i} className="rounded-xl border border-teal-100 bg-white p-3" data-testid={`voice-note-${i}`}>
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <div className="text-[10px] font-semibold uppercase tracking-wider text-teal-700">Voice note #{i + 1}</div>
                          {badge && (
                            <span
                              data-testid={`voice-note-lang-${i}`}
                              className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border ${badge.tone}`}
                              title={`Whisper auto-detected language: ${lang}`}
                            >
                              🌐 {badge.label}
                            </span>
                          )}
                        </div>
                        <audio
                          controls
                          preload="none"
                          src={v.url}
                          className="w-full h-10"
                          data-testid={`voice-note-audio-${i}`}
                        >
                          Your browser does not support the audio element.
                        </audio>
                        {v.transcript && (
                          <div className="mt-2 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
                            <span className="text-[10px] font-semibold uppercase tracking-wider text-teal-700 mr-1">Transcript:</span>
                            {v.transcript}
                          </div>
                        )}
                        {v.at && (
                          <div className="mt-1 text-[10px] text-slate-400">
                            Captured {new Date(v.at).toLocaleString()} · step {v.captured_at_state || "—"}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </Card>
            )}

            {isDoctor && data.payment_status === "paid" && (
              <CaseAiSummary consultationId={id} initial={data.ai_summary}/>
            )}

            <Card className="p-6 border-slate-200">
              <div className="font-heading text-lg font-semibold text-slate-900">Scans &amp; files</div>
              {(data.files?.length === 0 && !data.dicom_uploads?.length) && <p className="text-sm text-slate-500 mt-2">No files uploaded.</p>}
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {data.files?.map(f => {
                  const sizeMB = (f.size || 0) / (1024 * 1024);
                  const sizeLabel = sizeMB >= 1 ? `${sizeMB.toFixed(1)} MB` : `${Math.round((f.size||0)/1024)} KB`;
                  return (
                    <a key={f.id} href={`${API}/files/${f.id}/download?auth=${t}`} target="_blank" rel="noreferrer"
                       className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 bg-white hover:bg-slate-50"
                       data-testid={`file-${f.id}`}>
                      <div className="w-10 h-10 rounded-md bg-blue-50 grid place-items-center text-blue-700"><FileText className="w-5 h-5"/></div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-slate-900 truncate">{f.original_filename}</div>
                        <div className="text-xs text-slate-500">
                          {sizeLabel}
                          {f.kind === "dicom" ? " · DICOM" : ""}
                        </div>
                      </div>
                      <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200 shrink-0" title="Regular file — Emergent Object Storage">
                        Regular
                      </span>
                    </a>
                  );
                })}
                {data.dicom_uploads?.map(u => (
                  <button key={u.id} onClick={async () => {
                    try {
                      const r = await api.get(`/admin/dicom-uploads/${u.id}/download-url`);
                      window.open(r.data.url, "_blank");
                    } catch (e) { toast.error("Could not open file"); }
                  }} className="flex items-center gap-3 p-3 rounded-lg border border-indigo-200 bg-indigo-50/40 hover:bg-indigo-50 text-left" data-testid={`dicom-r2-${u.id}`}>
                    <div className="w-10 h-10 rounded-md bg-indigo-100 grid place-items-center text-indigo-700"><FileText className="w-5 h-5"/></div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-slate-900 truncate">{u.filename}</div>
                      <div className="text-xs text-slate-500">
                        {((u.size || 0) / (1024 * 1024 * 1024)).toFixed(2)} GB · Large DICOM study
                        {u.extract_count ? ` · ${u.extract_count} slices` : ""}
                      </div>
                    </div>
                    <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-indigo-600 text-white shrink-0" title="Uploaded via secure upload link, stored on Cloudflare R2">
                      Large · R2
                    </span>
                  </button>
                ))}
              </div>

              {/* In-viewer slice browsing for auto-extracted ZIP uploads */}
              {data.dicom_uploads?.filter(u => (u.filename || "").toLowerCase().endsWith(".zip")).map(u => (
                <div key={`slices-${u.id}`} className="mt-4" data-testid={`slice-browser-${u.id}`}>
                  <DicomSliceBrowser uploadRowId={u.id} authToken={t} />
                </div>
              ))}
              {/* In-browser preview for every DICOM in the case */}
              {data.files?.filter(f => f.kind === "dicom").map(f => (
                <div key={`viewer-${f.id}`} className="mt-4" data-testid={`dicom-preview-${f.id}`}>
                  <DicomViewer fileId={f.id} authToken={t} originalName={f.original_filename} />
                </div>
              ))}
            </Card>

            {/* Large DICOM (R2) upload manager — admin / doctor / hospital only */}
            {(user?.role === "admin" || isDoctor || user?.role === "hospital") && (
              <DicomUploadManager caseId={data.id} />
            )}

            {/* Opinion */}
            <Card className="p-6 border-slate-200">
              <div className="flex items-center justify-between">
                <div className="font-heading text-lg font-semibold text-slate-900 flex items-center gap-2">
                  <Stethoscope className="w-5 h-5 text-blue-700"/> Second Opinion
                </div>
                {data.opinion && <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">Delivered</span>}
              </div>
              {data.opinion && !isDoctor ? (
                <div className="mt-3 space-y-3 text-sm" data-testid="patient-opinion-view">
                  {data.opinion.structured?.fields ? (
                    <StructuredReportView structured={data.opinion.structured} doctor={data}/>
                  ) : (
                    <>
                      <Field label="Diagnosis summary" value={data.opinion.diagnosis_summary}/>
                      <Field label="Recommendations" value={data.opinion.recommendations}/>
                      {data.opinion.prescription && <Field label="Prescription" value={data.opinion.prescription}/>}
                    </>
                  )}
                  <div className="text-slate-500 text-xs">Follow-up required: {data.opinion.follow_up_required ? "Yes" : "No"}</div>
                </div>
              ) : isDoctor && data.payment_status === "paid" ? (
                <div className="mt-3 space-y-3">
                  {data.status === "awaiting_review" && (
                    <div className="flex items-center justify-between rounded-lg border border-sky-200 bg-sky-50 p-3">
                      <div className="text-sm text-sky-800">
                        <div className="font-semibold">Ready for your review</div>
                        <div className="text-xs">Tap <b>Start review</b> to notify the patient that you're looking at their case.</div>
                      </div>
                      <Button onClick={startReview} className="bg-sky-700 hover:bg-sky-800" data-testid="start-review-btn">
                        Start review
                      </Button>
                    </div>
                  )}

                  {/* Structured / Free-form mode toggle */}
                  <div className="flex items-center gap-2 p-1 rounded-lg bg-slate-100 w-fit" data-testid="opinion-mode-toggle">
                    <button
                      type="button"
                      onClick={() => setOpinionMode("structured")}
                      className={`px-3 h-8 rounded-md text-xs font-semibold transition ${
                        opinionMode === "structured"
                          ? "bg-white text-slate-900 shadow-sm"
                          : "text-slate-500 hover:text-slate-700"
                      }`}
                      data-testid="mode-structured"
                    >
                      Structured
                    </button>
                    <button
                      type="button"
                      onClick={() => setOpinionMode("freeform")}
                      className={`px-3 h-8 rounded-md text-xs font-semibold transition ${
                        opinionMode === "freeform"
                          ? "bg-white text-slate-900 shadow-sm"
                          : "text-slate-500 hover:text-slate-700"
                      }`}
                      data-testid="mode-freeform"
                    >
                      Free-form
                    </button>
                  </div>

                  {opinionMode === "structured" ? (
                    <StructuredOpinionEditor
                      consultation={data}
                      value={structured}
                      onChange={setStructured}
                    />
                  ) : (
                  <>
                  <div>
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <Label>Diagnosis summary</Label>
                      <VoiceDictationButton
                        value={opinion.diagnosis_summary}
                        onAppend={(v) => setOpinion({ ...opinion, diagnosis_summary: v })}
                        langCode={lang}
                        testId="voice-diagnosis-btn"
                      />
                    </div>
                    <Textarea rows={3} value={opinion.diagnosis_summary} onChange={e => setOpinion({...opinion, diagnosis_summary: e.target.value})} data-testid="opinion-diagnosis"/>
                  </div>
                  <div>
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <Label>Recommendations</Label>
                      <VoiceDictationButton
                        value={opinion.recommendations}
                        onAppend={(v) => setOpinion({ ...opinion, recommendations: v })}
                        langCode={lang}
                        testId="voice-recommendations-btn"
                      />
                    </div>
                    <Textarea rows={4} value={opinion.recommendations} onChange={e => setOpinion({...opinion, recommendations: e.target.value})} data-testid="opinion-recommendations"/>
                  </div>
                  <div>
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <Label>Prescription (optional)</Label>
                      <VoiceDictationButton
                        value={opinion.prescription}
                        onAppend={(v) => setOpinion({ ...opinion, prescription: v })}
                        langCode={lang}
                        testId="voice-prescription-btn"
                      />
                    </div>
                    <Textarea rows={2} value={opinion.prescription} onChange={e => setOpinion({...opinion, prescription: e.target.value})} data-testid="opinion-prescription"/>
                  </div>
                  </>
                  )}
                  <div className="flex items-center gap-3">
                    <Switch checked={opinion.follow_up_required} onCheckedChange={(v) => setOpinion({...opinion, follow_up_required: v})} id="fu" data-testid="opinion-followup"/>
                    <Label htmlFor="fu" className="m-0">Follow-up required</Label>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={polishOpinion}
                      disabled={polishBusy}
                      data-testid="polish-opinion-btn"
                      className="border-blue-200 text-blue-700 hover:bg-blue-50"
                    >
                      {polishBusy
                        ? (<><Sparkles className="w-4 h-4 mr-1.5 animate-pulse"/> Polishing…</>)
                        : (<><Sparkles className="w-4 h-4 mr-1.5"/> Polish with AI</>)}
                    </Button>
                    <Button onClick={submitOpinion} className="bg-blue-700 hover:bg-blue-800" data-testid="submit-opinion-btn">
                      {data.opinion ? "Update report" : "Submit report & notify patient"}
                    </Button>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-slate-500 mt-2">
                  {data.payment_status !== "paid" ? "Awaiting payment from patient." :
                   data.status === "under_review" ? "Doctor is reviewing your case. You'll be notified when the report is ready." :
                   data.status === "awaiting_review" ? "Payment received. Doctor will begin review shortly." :
                   "Awaiting opinion from doctor."}
                </p>
              )}
            </Card>

            {/* Video call */}
            <Card className="p-6 border-slate-200">
              <div className="flex items-center gap-2 font-heading text-lg font-semibold text-slate-900">
                <Video className="w-5 h-5 text-blue-700"/> Video consultation
              </div>
              <p className="text-sm text-slate-500 mt-1">Free end-to-end encrypted call powered by Jitsi Meet. Room: <span className="font-mono text-xs">{roomName}</span></p>

              {/* Live-call banner — appears to the OTHER party when a call was just started */}
              {incomingCall && (
                <div
                  className="mt-3 rounded-xl border border-emerald-300 bg-emerald-50 px-4 py-3 flex items-center gap-3 animate-pulse"
                  data-testid="incoming-call-banner"
                  role="alert"
                >
                  <span className="relative flex h-3 w-3 shrink-0">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-600"></span>
                  </span>
                  <div className="flex-1 min-w-0 text-sm text-emerald-900">
                    <div className="font-semibold">Live video call in progress</div>
                    <div className="text-xs text-emerald-800/80">Tap “Join call” now to connect.</div>
                  </div>
                  <Button
                    size="sm"
                    onClick={joinCall}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="incoming-call-accept-btn"
                  >
                    <PhoneCall className="w-4 h-4 mr-1.5"/> Join now
                  </Button>
                </div>
              )}

              <div className="mt-3 flex gap-2 flex-wrap">
                <Button
                  className={callActive && !callStartedByMe ? "bg-emerald-600 hover:bg-emerald-700" : "bg-blue-700 hover:bg-blue-800"}
                  onClick={joinCall}
                  data-testid="video-join-btn"
                >
                  <Video className="w-4 h-4 mr-2"/>{callActive && !callStartedByMe ? "Join live call" : "Start / Join call"}
                </Button>
                <a href={`https://meet.jit.si/${roomName}`} target="_blank" rel="noreferrer">
                  <Button variant="outline" data-testid="video-open-tab">Open in new tab</Button>
                </a>
              </div>

              {/* Fallback contact buttons — open WhatsApp / dial directly */}
              {data?.counterparty_phone && (
                <div className="mt-3 pt-3 border-t border-slate-100">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">
                    Can't connect on video? Reach the {isDoctor ? "patient" : "doctor"} directly
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <a
                      href={`https://wa.me/${String(data.counterparty_phone).replace(/[^0-9]/g, "")}?text=${encodeURIComponent(
                        `Hi, this is ${user?.name || ""} from NeuroOpinion. I'm calling you for case #${id.slice(0,8)}. Please join the video call here: ${window.location.origin}/case/${id}?call=1`
                      )}`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="wa-chat-btn"
                    >
                      <Button type="button" className="bg-green-600 hover:bg-green-700 text-white">
                        <MessageCircle className="w-4 h-4 mr-2"/> Open WhatsApp
                      </Button>
                    </a>
                    <a href={`tel:${String(data.counterparty_phone).replace(/\s+/g, "")}`} data-testid="tel-call-btn">
                      <Button type="button" variant="outline" className="border-slate-300">
                        <Phone className="w-4 h-4 mr-2"/> Phone call
                      </Button>
                    </a>
                  </div>
                  <p className="mt-2 text-[11px] text-slate-500">
                    WhatsApp opens with a pre-filled message and a one-tap link the other party can tap to instantly join the video call.
                  </p>
                </div>
              )}
              <p className="mt-3 text-xs text-slate-500">
                Tip: after you tap “Start call”, the other person sees a <span className="text-emerald-700 font-medium">live “Join now”</span> banner and a notification, so they can join instantly. Works best in the <span className="font-medium">Chrome/Safari browser</span>; if camera/mic don't start inside the app, use “Open in new tab”.
              </p>
            </Card>

            {/* Digital prescription (Phase 4 — TMG 2020 compliant) */}
            {data.payment_status === "paid" && (
              <Card className="p-6 border-slate-200" data-testid="prescription-card">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2 font-heading text-lg font-semibold text-slate-900">
                      <ClipboardPlus className="w-5 h-5 text-blue-700"/> Digital prescription
                    </div>
                    <p className="text-sm text-slate-500 mt-1">
                      {isDoctor
                        ? "Issue a TMG-2020 compliant Rx after the consultation. Patient gets a download link instantly."
                        : "Prescriptions issued by your doctor after the consultation will appear here as downloadable PDFs."}
                    </p>
                  </div>
                  {isDoctor && (
                    <Button
                      onClick={() => setRxOpen(true)}
                      className="bg-blue-700 hover:bg-blue-800"
                      data-testid="rx-issue-btn"
                    >
                      <ClipboardPlus className="w-4 h-4 mr-2"/> Issue prescription
                    </Button>
                  )}
                </div>
                <div className="mt-4">
                  <PrescriptionsList consultationId={id} refreshKey={rxRefresh}/>
                </div>
              </Card>
            )}

            {/* Patient review request — shown only after the doctor has submitted the opinion */}
            {!isDoctor && user?.role === "patient" && data.status === "completed" && (
              <BookVideoFollowup
                consultationId={id}
                onBooked={(newCid) => navigate(`/case/${newCid}#pay`)}
              />
            )}
            {!isDoctor && user?.role === "patient" && data.status === "completed" && (
              <ReviewCard consultationId={id} doctorName={data.doctor_name}/>
            )}
          </div>

          {/* Right: Chat */}
          <Card className="p-0 border-slate-200 flex flex-col h-[640px]">
            <div className="px-4 py-3 border-b border-slate-200 font-heading font-semibold text-slate-900">Chat</div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
              {msgs.length === 0 && <div className="text-xs text-slate-500 text-center mt-4">No messages yet.</div>}
              {msgs.map(m => {
                const mine = m.sender_id === user?.id;
                const isAdmin = m.sender_role === "admin";
                return (
                  <div key={m.id} className={`flex ${mine?"justify-end":"justify-start"}`}>
                    <div className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm ${
                      mine
                        ? "bg-blue-700 text-white rounded-br-sm"
                        : isAdmin
                          ? "bg-fuchsia-50 text-slate-900 border border-fuchsia-200 rounded-bl-sm"
                          : "bg-white text-slate-900 border border-slate-200 rounded-bl-sm"
                    }`} data-testid={`msg-${m.id}`}>
                      {!mine && (
                        <div className={`text-[10px] uppercase tracking-wider ${isAdmin ? "text-fuchsia-700 font-bold" : "opacity-70"}`}>
                          {isAdmin ? "NeuroOpinion team" : m.sender_name}
                        </div>
                      )}
                      {m.text}
                    </div>
                  </div>
                );
              })}
              <div ref={bottomRef}/>
            </div>
            <div className="p-3 border-t border-slate-200 flex items-center gap-2">
              <Input value={text} onChange={e => setText(e.target.value)} onKeyDown={e => e.key==="Enter" && send()} placeholder="Type a message…" data-testid="chat-input"/>
              <VoiceDictationButton
                value={text}
                onAppend={setText}
                langCode={lang}
                testId="chat-voice-btn"
                className="shrink-0"
              />
              <Button onClick={send} className="bg-blue-700 hover:bg-blue-800" data-testid="chat-send-btn"><Send className="w-4 h-4"/></Button>
            </div>
          </Card>
        </div>
      </div>

      {/* Video call dialog (Jitsi) */}
      <Dialog open={videoOpen} onOpenChange={setVideoOpen}>
        <DialogContent className="max-w-5xl w-[95vw] p-0 overflow-hidden">
          <DialogHeader className="px-5 py-3 border-b border-slate-200 flex-row items-center justify-between">
            <DialogTitle className="font-heading flex items-center gap-2 text-base">
              <Video className="w-4 h-4 text-blue-700"/> Video consultation
            </DialogTitle>
          </DialogHeader>
          {videoOpen && (
            <iframe
              key={roomName}
              title="Jitsi call"
              src={`https://meet.jit.si/${roomName}#userInfo.displayName=%22${encodeURIComponent(user?.name || 'User')}%22&config.prejoinPageEnabled=false&config.startWithVideoMuted=false`}
              allow="camera; microphone; fullscreen; display-capture; autoplay"
              className="w-full h-[70vh] border-0"
              data-testid="jitsi-frame"
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Phase 4 — Digital prescription dialog (doctor only) */}
      <PrescriptionDialog
        open={rxOpen}
        onClose={() => setRxOpen(false)}
        consultationId={id}
        defaultDiagnosis={data?.opinion?.diagnosis_summary || opinion.diagnosis_summary || ""}
        onIssued={() => setRxRefresh((k) => k + 1)}
      />
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{label}</div>
      <div className="mt-1 text-slate-800 whitespace-pre-wrap">{value}</div>
    </div>
  );
}

/**
 * Patient-side structured report viewer — renders the doctor's filled-out
 * template as a clean sectioned report. Falls back to a generic key/value
 * list if the template id is unknown (e.g. doctor was on a newer build).
 */
function StructuredReportView({ structured, doctor }) {
  const tpl = TEMPLATE_BY_ID[structured.template] || TEMPLATE_BY_ID.general;
  const f = structured.fields || {};
  const TplIcon = tpl?.icon;
  // Build ordered section list using the template's own sections so the
  // doctor's intent is preserved, even if a field is empty.
  const filled = (tpl?.sections || []).filter((s) => (f[s.key] || "").trim().length > 0);
  // If template is unknown, just dump whatever keys are present.
  const fallbackSections = !tpl?.sections ? Object.entries(f).map(([k, v]) => ({ key: k, label: k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()), val: v })) : null;

  return (
    <div data-testid="structured-report-view">
      <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-200">
        {TplIcon && <span className={`shrink-0 w-9 h-9 rounded-lg bg-gradient-to-br ${tpl.color || "from-slate-500 to-slate-700"} text-white grid place-items-center`}><TplIcon className="w-5 h-5"/></span>}
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Structured second opinion</div>
          <div className="font-heading font-semibold text-slate-900 text-base">{tpl?.label || "Neuro report"}</div>
        </div>
      </div>

      <div className="space-y-2.5">
        {(filled.length ? filled : (fallbackSections || [])).map((s) => (
          <div key={s.key} className="rounded-lg border border-slate-200 bg-white p-3 hover:border-blue-300 transition-colors">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-blue-700">{s.label}</div>
            <div className="mt-1 text-slate-800 whitespace-pre-wrap text-sm leading-relaxed">{s.val || f[s.key]}</div>
          </div>
        ))}
      </div>

      {/* Medico-legal footer */}
      <div className="mt-5 pt-3 border-t border-dashed border-slate-300 text-[11px] text-slate-500 leading-relaxed" data-testid="report-medico-legal-footer">
        <div className="font-semibold text-slate-700">
          {doctor?.doctor_name
            ? (/^dr\.?\s/i.test(doctor.doctor_name.trim()) ? doctor.doctor_name : `Dr. ${doctor.doctor_name}`)
            : "Verified specialist"}
          {doctor?.doctor_registration_number ? ` · MCI/NMC ${doctor.doctor_registration_number}` : ""}
        </div>
        <div className="mt-1">
          This is a second-opinion document delivered by NeuroOpinion India and does not replace your treating doctor's plan. For emergencies, please call 108 or visit the nearest hospital.
        </div>
      </div>
    </div>
  );
}